'use client'

import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { useLearningRecord } from '@/hooks/useLearningRecord'
import { useTranslation } from 'react-i18next';
import {
  R, PrintFrame, PrintButton, RecordHeader, StatRow,
  StandardStatement, SupervisorBlock, scoreTone, fmtDate, FeedbackBlock,
  resolveRecordVariant, getRecordCopy,
} from '../records/recordShared'

export default function LearningRecordPage() {
  const { t } = useTranslation();

  const { childId, classId } = useParams<{ childId: string; classId: string }>()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { userProfile } = useAuth()
  const { child, courses, feedback, loading, error } = useLearningRecord(childId, classId)
  const course = courses[0]

  const isFaithSubject = Boolean(
    course && ((course.subjectGroup || '').toLowerCase().includes('bible') ||
               (course.subjectGroup || '').toLowerCase().includes('theology') ||
               (course.subjectGroup || '').toLowerCase().includes('faith') ||
               (course.subjectGroup || '').toLowerCase().includes('ministry'))
  )

  const variant = resolveRecordVariant({
    searchContext: searchParams.get('context'),
    searchVariant: searchParams.get('variant'),
    childOrgType: child?.orgType,
    childFacilityType: child?.facilityType,
    userOrgType: (userProfile as any)?.facility_org_type,
    userFacilityType: userProfile?.facility_type,
    isFaithSubject,
  })

  const copy = getRecordCopy(variant, child?.orgName)
  const feedbackLabel = copy.feedbackLabel

  const backTarget = variant === 'church'
    ? '/church/learn'
    : (!childId || childId === 'me') ? '/learn' : `/homeschool/child/${childId}`

  if (loading) {
    return <div style={{ minHeight: '100dvh', background: '#ECE9E1', display: 'flex', alignItems: 'center', justifyContent: 'center', color: R.slate }}>
      Loading learning record…
    </div>
  }

  if (error || !child || !course) {
    return (
      <PrintFrame maxWidth={640}>
        <RecordHeader
          eyebrow={copy.learningRecordEyebrow}
          title="Record unavailable"
        />
        <p style={{ color: R.slate, fontSize: 14 }}>{error || 'No record found for this student and course.'}</p>
        <Link to={backTarget} className="no-print"
          style={{ display: 'inline-block', marginTop: 16, color: R.gold, fontWeight: 700, fontSize: 13, textDecoration: 'none' }}>
          ← Back
        </Link>
      </PrintFrame>
    )
  }

  // ── Derived summary (session time: derived evidence, honestly labeled) ──
  const quizSeconds = course.lessons.reduce((s, l) => s + (l.quiz?.durationSeconds ?? 0), 0)
  const sessionHours = (quizSeconds / 3600).toFixed(1)
  const completedCount = course.lessons.filter(l => l.completed).length
  const status = course.completedOn ? 'Completed' : completedCount > 0 ? 'In progress' : 'Not started'

  // Modules with any evidence — grouped render
  const modules = course.plan.map((m, mi) => ({
    title: m.title, pos: mi + 1,
    lessons: course.lessons.filter(l => l.modulePos === mi + 1),
  }))

  return (
    <PrintFrame>
      <div className="no-print" style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 20 }}>
        <button onClick={() => navigate(backTarget)}
          style={{ background: 'none', border: 'none', color: R.slate, fontSize: 13, fontWeight: 700, cursor: 'pointer', padding: 0 }}>
          ← Back
        </button>
        <PrintButton label={t('learn.print_record_pdf')} />
      </div>

      <RecordHeader
        eyebrow={copy.learningRecordEyebrow}
        title={`Learning Record — ${course.className}`}
        subtitle={`${child.name}${child.age && variant === 'homeschool' ? `, age ${child.age}` : ''} · ${course.subjectGroup} · ${status}`}
        right={<PrintButton label={t('learn.print_record_pdf')} />}
      />

      <StatRow items={[
        { label: 'Modules', value: course.plan.length },
        { label: 'Lessons', value: course.planLessons },
        { label: 'Concepts taught', value: course.planConcepts },
        { label: 'Lessons completed', value: `${completedCount}/${course.planLessons}` },
        { label: 'Course mastery', value: `${Math.round(course.mastery)}%` },
      ]} />

      {/* Record period */}
      <div className="print-block" style={{ display: 'flex', gap: 24, fontSize: 12.5, color: R.slate, marginBottom: 22, flexWrap: 'wrap' }}>
        <span><strong style={{ color: R.ink }}>{t('learn.record_period')}</strong> {fmtDate(course.dateRange.first)} – {fmtDate(course.dateRange.last)}</span>
        <span><strong style={{ color: R.ink }}>{t('learn.completed_on')}</strong> {course.completedOn ? fmtDate(course.completedOn) : '—'}</span>
        <span><strong style={{ color: R.ink }}>{t('learn.instructional_session_time_derived_from_tutoring')}</strong> ~{sessionHours}h</span>
      </div>

      {/* Lesson evidence table, per module */}
      {modules.map(m => (
        <div key={m.pos} style={{ marginBottom: 14 }}>
          <div className="module-header"
            style={{ background: R.cream, border: `1px solid ${R.border}`, borderRadius: 10, padding: '10px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontWeight: 800, fontSize: 13.5, color: R.ink }}>{m.pos}. {m.title}</span>
            <span style={{ fontSize: 11, color: R.muted }}>
              {m.lessons.filter(l => l.completed).length}/{m.lessons.length} done
            </span>
          </div>

          {m.lessons.map(l => (
            <div key={`${l.modulePos}-${l.lessonPos}`} className="print-block"
              style={{
                display: 'grid', gridTemplateColumns: '26px 1fr 64px 84px 30px', gap: 8,
                alignItems: 'start', padding: '7px 12px', borderBottom: `1px solid ${R.border}`, fontSize: 12
              }}>
              <span style={{ color: R.goldSoft, fontWeight: 900, fontSize: 11 }}>
                {l.completed ? '✓' : l.quiz ? '◐' : ''}
              </span>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 700, color: R.ink, fontSize: 12.5 }}>{l.title}</div>
                {l.objective && <div style={{ fontSize: 10.5, color: R.muted, marginTop: 1 }}>{l.objective}</div>}
                {/* Retakes visible — reviewers read remediation as rigor */}
                {!!l.quiz && l.quiz.attempts > 1 && (
                  <span style={{ fontSize: 9.5, color: R.slate, fontStyle: 'italic' }}>
                    {l.quiz.attempts} attempts — passed on retry
                  </span>
                )}
              </div>
              <span style={{ fontWeight: 800, color: l.quiz ? scoreTone(l.quiz.best) : R.muted, fontVariantNumeric: 'tabular-nums', textAlign: 'right' }}>
                {l.quiz ? `${Math.round(l.quiz.best)}%` : '—'}
              </span>
              <span style={{ color: R.slate, fontSize: 11, textAlign: 'right' }}>
                {fmtDate(l.quiz?.lastAt ?? l.log?.completedAt)}
              </span>
              <span style={{ color: R.muted, fontSize: 10, textAlign: 'right' }}>{l.conceptCount || ''}</span>
            </div>
          ))}
        </div>
      ))}

      {/* Written work */}
      {course.essayMode && course.essayMode !== 'none' && course.essays.length > 0 && (
        <div style={{ marginTop: 20 }}>
          <div className="module-header" style={{ fontWeight: 800, fontSize: 14, color: R.ink, marginBottom: 8 }}>
            Written Work — {course.essayMode === 'essay_per_module' ? 'Module Essays' : course.essayMode === 'capstone_only' ? 'Capstone' : 'Short Written Answers'}
          </div>
          {course.essays.map((e, i) => (
            <div key={i} className="print-block"
              style={{ border: `1px solid ${R.border}`, borderRadius: 10, padding: '10px 14px', marginBottom: 8 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5 }}>
                <span style={{ fontWeight: 700, color: R.ink }}>Module {e.modulePos} — Written Assignment</span>
                <span style={{ fontWeight: 800, color: R.gold }}>
                  {e.score != null && e.maxScore ? `${Math.round((e.score / e.maxScore) * 100)}% (${e.score}/${e.maxScore})` : 'Graded'}
                </span>
              </div>
              {e.feedback && (
                <p style={{ margin: '6px 0 0', fontSize: 11.5, color: R.slate, lineHeight: 1.55 }}>
                  <strong>{t('learn.assessor_feedback')}</strong> {e.feedback.length > 320 ? `${e.feedback.slice(0, 320)}…` : e.feedback}
                </p>
              )}
              <div style={{ fontSize: 10.5, color: R.muted, marginTop: 4 }}>Submitted {fmtDate(e.submittedAt)}</div>
            </div>
          ))}
        </div>
      )}

      {/* Human feedback — teacher overrides + parent encouragement, printed */}
      {feedback.length > 0 && (
        <FeedbackBlock rows={feedback} headerLabel={feedbackLabel} />
      )}

      <StandardStatement variant={variant} orgName={child?.orgName} />
      <SupervisorBlock variant={variant} orgName={child?.orgName} />
      <p style={{ marginTop: 28, textAlign: 'center', fontSize: 10.5, color: R.muted }}>
        {copy.footer} · Mastery threshold 80% · Course mastery {Math.round(course.mastery)}%
      </p>
    </PrintFrame>
  )
}